/**
 * Narrowing the loaded list in the browser, the same way the policy list does:
 * the whole set is small (a hospital has tens of units, not thousands) and
 * every keystroke should narrow it without a round trip.
 */

import type { ScopeStatus, ScopeSummary } from './types';

export interface ScopeFilter {
  query: string;
  status: ScopeStatus | 'all';
  departmentId: string | 'all';
}

export const NO_FILTER: ScopeFilter = { query: '', status: 'all', departmentId: 'all' };

/**
 * Every word must match somewhere in the unit name, code or department name,
 * so "icu tower" narrows rather than widening to everything about either.
 */
export function filterScopes(
  rows: readonly ScopeSummary[],
  filter: ScopeFilter,
  departmentName: (id: string | null) => string,
): ScopeSummary[] {
  const words = filter.query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((row) => {
    if (filter.status !== 'all' && row.status !== filter.status) return false;
    if (filter.departmentId !== 'all' && row.departmentId !== filter.departmentId) return false;
    if (!words.length) return true;
    const hay = `${row.unitName} ${row.unitCode} ${departmentName(row.departmentId)}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** How many rows each status has, for the segmented control's labels. */
export function countByStatus(rows: readonly ScopeSummary[]): Record<ScopeStatus, number> {
  const c: Record<ScopeStatus, number> = { draft: 0, in_review: 0, approved: 0, retired: 0 };
  for (const r of rows) c[r.status] += 1;
  return c;
}
